// ============================================================
//  StarBridgeScene — mission state machine: 3 mini-games → restoration
// ============================================================
import { C, CS } from '../../constants/Colors.js';
import { Cosmo } from '../characters/Cosmo.js';
import { SaveManager } from '../../state/SaveManager.js';
import { audio } from '../../audio/AudioManager.js';
import { makeBuildQuestion, makePathQuestion, makeRepairQuestion, XP_BRIDGE } from '../../learning/math/PlaceValue.js';
import { CrystalBuilder } from '../minigames/CrystalBuilder.js';
import { NumberPath } from '../minigames/NumberPath.js';
import { RepairCore } from '../minigames/RepairCore.js';
import { addEnergy, ENERGY_PER_SEGMENT } from '../../progression/WorldEnergy.js';

type Phase = 'intro' | 'game1' | 'game2' | 'game3' | 'challenge' | 'restoring' | 'done';

export class StarBridgeScene extends Phaser.Scene {
  private cosmo!: Cosmo;
  private phase: Phase = 'intro';
  private bridgeSegGfx: Phaser.GameObjects.Graphics[] = [];
  private energyBars: Phaser.GameObjects.Graphics[] = [];
  private progressText!: Phaser.GameObjects.Text;
  private segmentsDone = 0;
  private envG!: Phaser.GameObjects.Graphics;

  constructor() { super({ key: 'StarBridge' }); }

  create() {
    SaveManager.setLastScene('StarBridge');
    const W = this.scale.width, H = this.scale.height;

    this.segmentsDone = SaveManager.getWorld('number-kingdom').bridgeSegments;

    this.drawEnvironment(W, H);
    this.createBridge(W, H);
    this.createCosmo(W, H);
    this.createHUD(W, H);

    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main, alpha: 1, duration: 700,
      onComplete: () => this.beginPhase('intro'),
    });
  }

  // ── Environment ──────────────────────────────────────────────
  private drawEnvironment(W: number, H: number) {
    this.envG = this.add.graphics();
    const g = this.envG;

    // Sky
    g.fillGradientStyle(0x050015, 0x050015, 0x0d0230, 0x0d0230, 1);
    g.fillRect(0, 0, W, H);

    // Stars
    for (let i = 0; i < 150; i++) {
      g.fillStyle(0xffffff, 0.1 + Math.random() * 0.7);
      g.fillCircle(Math.random() * W, Math.random() * H * 0.6, 0.4 + Math.random() * 1.2);
    }

    // Distant Number Kingdom castle (right)
    const cx = W * 0.82, cy = H * 0.3;
    g.fillStyle(0x1e0d30, 0.6); g.fillRect(cx - 18, cy, 36, 70);
    g.fillStyle(0x2d1558, 0.5); g.fillRect(cx - 16, cy, 32, 55);
    g.fillStyle(C.nkAmber, 0.4); g.fillRect(cx - 5, cy + 15, 10, 16);
    [-24, -8, 8, 24].forEach(bx => { g.fillStyle(0x3d2070, 0.5); g.fillRect(cx + bx - 4, cy - 8, 6, 8); });
    g.fillStyle(0x2d1558, 0.4); g.fillRect(cx - 32, cy + 14, 20, 48); g.fillRect(cx + 12, cy + 14, 20, 48);
    g.fillStyle(C.nkAmber, 0.1); g.fillEllipse(cx, cy + 35, 120, 80);

    // Left arrival island base
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(0x7b2fff, 0.025 * i); g.fillEllipse(W * 0.2, H * 0.82, 300 + i * 20, 100 + i * 10);
    }
    g.fillStyle(0x1a1228, 1); g.fillEllipse(W * 0.2, H * 0.84, 290, 80);
    g.fillStyle(0x251840, 1); g.fillEllipse(W * 0.2, H * 0.8, 280, 66);
    g.fillStyle(0x2d1558, 1); g.fillEllipse(W * 0.2, H * 0.77, 268, 55);
    g.fillStyle(0x3d2070, 0.8); g.fillEllipse(W * 0.2, H * 0.75, 250, 44);

    // Right destination island
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(C.nkAmber, 0.03 * i); g.fillEllipse(W * 0.82, H * 0.82, 200 + i * 15, 80 + i * 10);
    }
    g.fillStyle(0x1a1228, 1); g.fillEllipse(W * 0.82, H * 0.85, 190, 65);
    g.fillStyle(0x251840, 1); g.fillEllipse(W * 0.82, H * 0.81, 178, 55);
    g.fillStyle(0x2d1a50, 1); g.fillEllipse(W * 0.82, H * 0.78, 165, 46);
    g.fillStyle(0x3d2860, 0.8); g.fillEllipse(W * 0.82, H * 0.76, 152, 38);

    // Floating number crystals
    [[W * 0.08, H * 0.35], [W * 0.92, H * 0.32], [W * 0.5, H * 0.18]].forEach(([x, y]) => {
      for (let i = 3; i >= 1; i--) {
        g.fillStyle(0xff8c00, 0.04 * i); g.fillTriangle(x, y - 24 - i * 4, x - 10 - i * 2, y, x + 10 + i * 2, y);
      }
      g.fillStyle(0x5a1000, 1); g.fillTriangle(x, y - 24, x - 9, y, x + 9, y);
      g.fillStyle(0xff6b00, 0.8); g.fillTriangle(x, y - 18, x - 6, y - 2, x + 6, y - 2);
      g.fillStyle(0xffd700, 0.4); g.fillTriangle(x, y - 12, x - 3, y - 4, x + 3, y - 4);
    });

    // Bridge pillars
    const pillarY = H * 0.73;
    [W * 0.35, W * 0.65].forEach(px => {
      g.fillStyle(0x2d1558, 1); g.fillRect(px - 12, pillarY - 40, 24, 90);
      g.fillStyle(0x3d2070, 0.9); g.fillRect(px - 14, pillarY - 48, 28, 16);
      // Chain anchors
      g.lineStyle(1.5, 0x3d2060, 0.7);
      g.lineBetween(px - 6, pillarY - 32, px - 6, pillarY - 10);
      g.lineBetween(px + 6, pillarY - 32, px + 6, pillarY - 10);
    });

    // Ambient particles
    try {
      this.add.particles(W * 0.5, H * 0.35, 'particle_gold', {
        speed: { min: 8, max: 30 },
        angle: { min: 0, max: 360 },
        scale: { min: 0.3, max: 0.8 },
        alpha: { start: 0.5, end: 0 },
        lifespan: { min: 2000, max: 4000 },
        quantity: 1,
        frequency: 350,
        gravityY: -15,
      });
    } catch { /* ok */ }
  }

  // ── Bridge segments ──────────────────────────────────────────
  private createBridge(W: number, H: number) {
    const bridgeY = H * 0.73;
    const startX  = W * 0.36;
    const segW    = 68, gap = 4;
    const count   = 4;

    this.bridgeSegGfx = [];
    this.energyBars  = [];

    for (let i = 0; i < count; i++) {
      const sx = startX + i * (segW + gap);
      const sy = bridgeY + (i === 1 || i === 2 ? 16 : 6);

      const g = this.add.graphics();
      this.bridgeSegGfx.push(g);

      if (i < this.segmentsDone) {
        this.drawLitSeg(g, sx, sy);
        g.setAngle(0);
      } else {
        g.setAngle(i === 1 ? -14 : i === 2 ? 11 : 0);
        this.drawDarkSeg(g, sx + (i === 1 ? -6 : i === 2 ? 4 : 0), sy + (i === 1 || i === 2 ? 10 : 0));
      }

      // Energy bar above each segment
      const barG = this.add.graphics();
      this.drawSegBar(barG, sx + segW / 2, sy - 18, i < this.segmentsDone ? 1 : 0);
      this.energyBars.push(barG);
    }
  }

  private drawDarkSeg(g: Phaser.GameObjects.Graphics, x: number, y: number) {
    g.clear();
    g.fillStyle(0x1a0a2e, 1); g.fillRoundedRect(x, y, 68, 24, 5);
    g.fillStyle(0x251840, 0.8); g.fillRoundedRect(x + 2, y + 2, 64, 10, 4);
    g.lineStyle(1, 0x3d2060, 0.5); g.strokeRoundedRect(x, y, 68, 24, 5);
    [x + 14, x + 32, x + 50].forEach(cx2 => { g.lineStyle(1, 0x3d2060, 0.25); g.lineBetween(cx2, y + 2, cx2, y + 22); });
    g.lineStyle(1, 0x5a3080, 0.35); g.lineBetween(x + 18, y + 6, x + 26, y + 18); g.lineBetween(x + 40, y + 4, x + 34, y + 20);
    g.setData('sx', x); g.setData('sy', y);
  }

  private drawLitSeg(g: Phaser.GameObjects.Graphics, x: number, y: number) {
    g.clear();
    for (let i = 4; i >= 1; i--) {
      g.fillStyle(0xff8c00, 0.05 * i); g.fillRoundedRect(x - i * 3, y - i * 2, 68 + i * 6, 24 + i * 4, 7);
    }
    g.fillStyle(0x3d2000, 1); g.fillRoundedRect(x, y, 68, 24, 5);
    g.fillStyle(0xff4500, 0.9); g.fillRoundedRect(x + 2, y + 2, 64, 10, 4);
    g.fillStyle(0xff8c00, 0.5); g.fillRoundedRect(x + 4, y + 3, 38, 5, 3);
    g.fillStyle(0xffd700, 0.25); g.fillRoundedRect(x + 5, y + 4, 18, 3, 2);
    g.lineStyle(2, 0xffcc00, 0.9); g.strokeRoundedRect(x, y, 68, 24, 5);
    [x + 14, x + 32, x + 50].forEach(cx2 => { g.lineStyle(1.5, 0xff8c00, 0.4); g.lineBetween(cx2, y + 4, cx2, y + 20); });
    g.setData('sx', x); g.setData('sy', y);
  }

  private drawSegBar(g: Phaser.GameObjects.Graphics, cx: number, cy: number, pct: number) {
    g.clear();
    g.fillStyle(0x1a0a2e, 0.8); g.fillRoundedRect(cx - 30, cy - 5, 60, 8, 3);
    if (pct > 0) {
      g.fillStyle(0xff8c00, 1); g.fillRoundedRect(cx - 28, cy - 3, 56 * pct, 4, 2);
    }
    g.lineStyle(1, 0x3d2060, 0.5); g.strokeRoundedRect(cx - 30, cy - 5, 60, 8, 3);
  }

  // ── Cosmo ────────────────────────────────────────────────────
  private createCosmo(W: number, H: number) {
    this.cosmo = new Cosmo(this, W * 0.22, H * 0.63, 0.85);
    this.cosmo.setDepth(10);
  }

  // ── HUD ──────────────────────────────────────────────────────
  private createHUD(W: number, H: number) {
    // Back button
    const back = this.add.text(20, 20, '← Hub', {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '14px', color: CS.cyan,
      backgroundColor: '#0d0230', padding: { x: 10, y: 6 },
    }).setDepth(30).setInteractive({ cursor: 'pointer' });
    back.on('pointerdown', () => {
      this.cameras.main.fade(350, 0, 0, 20);
      this.time.delayedCall(380, () => this.scene.start('HomeHub'));
    });

    // Progress
    this.progressText = this.add.text(W * 0.5, 28, `Bridge Progress: ${this.segmentsDone}/4`, {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '14px', fontStyle: 'bold',
      color: CS.gold, stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5, 0.5).setDepth(30);

    // XP
    const xpText = this.add.text(W - 18, 28, `⭐ ${SaveManager.getProfile().xp} XP`, {
      fontFamily: '"Nunito", Arial, sans-serif', fontSize: '13px', color: CS.gold,
    }).setOrigin(1, 0.5).setDepth(30);
  }

  // ── Phase state machine ──────────────────────────────────────
  private beginPhase(p: Phase) {
    this.phase = p;

    switch (p) {
      case 'intro':   this.runIntro();   break;
      case 'game1':   this.runGame1();   break;
      case 'game2':   this.runGame2();   break;
      case 'game3':   this.runGame3();   break;
      case 'challenge': this.runChallenge(); break;
      case 'restoring': this.runRestoration(); break;
    }
  }

  private runIntro() {
    this.cosmo.say('Let\'s charge these bridge segments with place-value energy!', 3200);
    this.time.delayedCall(3600, () => this.beginPhase('game1'));
  }

  // Mini-game 1: Crystal Builder (build a number)
  private runGame1() {
    if (this.segmentsDone >= 1) { this.beginPhase('game2'); return; }

    const q = makeBuildQuestion('easy');
    this.cosmo.say(`Mission 1: Build  ${q.target}  using crystals!`, 2500);
    this.time.delayedCall(2800, () => {
      new CrystalBuilder(this, q, (ok) => {
        if (ok) this.completeSegment(0, 'game2');
      });
    });
  }

  // Mini-game 2: Number Path
  private runGame2() {
    if (this.segmentsDone >= 2) { this.beginPhase('game3'); return; }

    const q = makePathQuestion('easy');
    this.cosmo.say(`Mission 2: Navigate the number path to  ${q.pathEnd}!`, 2500);
    this.time.delayedCall(2800, () => {
      new NumberPath(this, q, (ok) => {
        if (ok) this.completeSegment(1, 'game3');
      });
    });
  }

  // Mini-game 3: Repair Core
  private runGame3() {
    if (this.segmentsDone >= 3) { this.beginPhase('challenge'); return; }

    const q = makeRepairQuestion('medium');
    this.cosmo.say('Mission 3: Find the broken number in the core!', 2500);
    this.time.delayedCall(2800, () => {
      new RepairCore(this, q, (ok) => {
        if (ok) this.completeSegment(2, 'challenge');
      });
    });
  }

  // Final challenge: one more build question
  private runChallenge() {
    if (this.segmentsDone >= 4) { this.beginPhase('restoring'); return; }

    const q = makeBuildQuestion('medium');
    this.cosmo.say(`Final challenge! Build  ${q.target}  and unlock the last segment!`, 2800);
    this.time.delayedCall(3100, () => {
      new CrystalBuilder(this, q, (ok) => {
        if (ok) this.completeSegment(3, 'restoring');
      });
    });
  }

  // ── Segment completion ───────────────────────────────────────
  private completeSegment(idx: number, nextPhase: Phase) {
    this.segmentsDone = idx + 1;
    SaveManager.setBridgeSegments('number-kingdom', this.segmentsDone);
    addEnergy('number-kingdom', ENERGY_PER_SEGMENT);

    this.progressText.setText(`Bridge Progress: ${this.segmentsDone}/4`);

    // Animate segment restoration
    const W = this.scale.width, H = this.scale.height;
    const startX = W * 0.36, segW = 68, gap = 4;
    const bridgeY = H * 0.73;
    const sx = startX + idx * (segW + gap);
    const sy = bridgeY + (idx === 1 || idx === 2 ? 16 : 6);

    const g = this.bridgeSegGfx[idx];
    if (!g) return;

    audio.bridgeSegment();
    this.cosmo.celebrate();

    // Un-tilt and move to correct position
    this.tweens.add({
      targets: g,
      angle: 0,
      x: 0, y: 0,
      duration: 600,
      ease: 'Back.easeOut',
    });

    // Fade to lit
    this.time.delayedCall(300, () => {
      g.clear();
      this.drawLitSeg(g, sx, sy);

      // Particle burst at segment
      try {
        const emitter = this.add.particles(sx + 34, sy + 12, 'particle_gold', {
          speed: { min: 60, max: 180 },
          angle: { min: -120, max: -90 },
          scale: { start: 1, end: 0 },
          lifespan: 900,
          quantity: 20,
          tint: [0xffd700, 0xff8c00, 0xff4500, 0x00e5ff],
        });
        this.time.delayedCall(300, () => emitter.stop());
        this.time.delayedCall(1200, () => emitter.destroy());
      } catch { /* ok */ }

      // Update energy bar
      this.drawSegBar(this.energyBars[idx], sx + segW / 2, sy - 18, 1);

      // Delay before next phase
      this.time.delayedCall(1600, () => {
        if (nextPhase === 'restoring') {
          this.cosmo.say('ALL SEGMENTS POWERED! Starting restoration!', 2500);
          this.time.delayedCall(2800, () => this.beginPhase('restoring'));
        } else {
          this.beginPhase(nextPhase);
        }
      });
    });
  }

  // ── Restoration cinematic ────────────────────────────────────
  private runRestoration() {
    const W = this.scale.width, H = this.scale.height;
    this.phase = 'restoring';

    SaveManager.addXP(XP_BRIDGE);
    SaveManager.addStars(3);
    SaveManager.setWorldRestored('number-kingdom');
    audio.restoration();

    // Camera shake
    this.cameras.main.shake(600, 0.012);

    // Flash all segments to gold
    this.time.delayedCall(200, () => {
      this.bridgeSegGfx.forEach((g, i) => {
        const sx = W * 0.36 + i * 72;
        const sy = H * 0.73 + (i === 1 || i === 2 ? 16 : 6);
        this.drawLitSeg(g, sx, sy);
        this.energyBars.forEach((b, j) => {
          const bx = W * 0.36 + j * 72;
          this.drawSegBar(b, bx + 34, H * 0.73 - 18, 1);
        });
      });
    });

    // Light waves sweeping across bridge
    for (let i = 0; i < 4; i++) {
      this.time.delayedCall(400 + i * 180, () => {
        const wave = this.add.graphics().setDepth(15);
        const sx = W * 0.36 + i * 72;
        const sy = H * 0.73;
        for (let r = 3; r >= 1; r--) {
          wave.fillStyle(0xffcc00, 0.1 * r); wave.fillCircle(sx + 34, sy + 12, 30 + r * 12);
        }
        this.tweens.add({ targets: wave, alpha: 0, scaleX: 3, scaleY: 3, duration: 800, onComplete: () => wave.destroy() });
      });
    }

    // Grand light burst from bridge center
    this.time.delayedCall(900, () => {
      const burst = this.add.graphics().setDepth(20);
      for (let r = 8; r >= 1; r--) {
        burst.fillStyle(0xff8c00, 0.05 * r); burst.fillCircle(W * 0.5, H * 0.73, 30 + r * 20);
      }
      burst.fillStyle(0xffd700, 0.4); burst.fillCircle(W * 0.5, H * 0.73, 25);
      burst.fillStyle(0xffffff, 0.6); burst.fillCircle(W * 0.5, H * 0.73, 12);
      this.tweens.add({ targets: burst, alpha: 0, scaleX: 6, scaleY: 4, duration: 1200, onComplete: () => burst.destroy() });

      // Cosmo mega-celebrate
      this.cosmo.celebrate();
      this.cosmo.say('THE STAR BRIDGE IS RESTORED! Number Kingdom is alive again! 🌟', 4000);

      // Massive particle release
      try {
        const mega = this.add.particles(W * 0.5, H * 0.73, 'particle_star', {
          speed: { min: 100, max: 320 },
          angle: { min: 0, max: 360 },
          scale: { start: 1.5, end: 0 },
          alpha: { start: 1, end: 0 },
          lifespan: 1400,
          quantity: 50,
          tint: [0xffd700, 0xff8c00, 0x00e5ff, 0xff4dbd, 0xffffff],
        });
        this.time.delayedCall(500, () => mega.stop());
        this.time.delayedCall(2000, () => mega.destroy());
      } catch { /* ok */ }
    });

    // Screen gold flash then transition to reward
    this.time.delayedCall(3500, () => {
      const flash = this.add.graphics().setDepth(50);
      flash.fillStyle(0xffd700, 0);
      flash.fillRect(0, 0, W, H);
      this.tweens.add({
        targets: flash, alpha: 0.9, duration: 600, ease: 'Cubic.easeIn',
        onComplete: () => {
          this.tweens.add({
            targets: flash, alpha: 0, duration: 300,
            onComplete: () => this.scene.start('Reward'),
          });
        },
      });
    });
  }
}
